import React, { useState, useEffect } from 'react';
import './PrivacyPolicy.css'; // external CSS

// ─── Data ──────────────────────────────────────────────
const policySections = [
  {
    id: 'intro',
    icon: 'fa-solid fa-shield-halved',
    title: 'Introduction',
    content: (
      <>
        <p>
          Welcome to <strong>OneClick Tech</strong> — your one‑click solution for
          booking trusted technicians. We take your privacy seriously.
          This policy explains how we collect, use, disclose, and safeguard
          your information when you use our platform.
        </p>
        <p>
          By using OneClick Tech, you agree to the collection and use of
          information in accordance with this policy. If you have any
          questions, please <a href="#contact" style={{ color: '#e5243b', textDecoration: 'none', fontWeight: 500 }}>contact us</a>.
        </p>
      </>
    )
  },
  {
    id: 'collect',
    icon: 'fa-solid fa-database',
    title: 'Information We Collect',
    content: (
      <>
        <p>
          To provide you with a seamless booking experience, we collect the
          following types of information:
        </p>
        <div className="info-grid">
          <div className="info-grid-item">
            <strong><i className="fa-regular fa-user" style={{ marginRight: '0.4rem', color: '#e5243b' }} /> Personal Data</strong>
            <span>Name, email, phone number, address, and payment details.</span>
          </div>
          <div className="info-grid-item">
            <strong><i className="fa-regular fa-clock" style={{ marginRight: '0.4rem', color: '#e5243b' }} /> Usage Data</strong>
            <span>Booking history, service preferences, and interaction logs.</span>
          </div>
          <div className="info-grid-item">
            <strong><i className="fa-solid fa-location-dot" style={{ marginRight: '0.4rem', color: '#e5243b' }} /> Location Data</strong>
            <span>IP address, device location (with your consent) for service dispatch.</span>
          </div>
          <div className="info-grid-item">
            <strong><i className="fa-regular fa-rectangle-ad" style={{ marginRight: '0.4rem', color: '#e5243b' }} /> Device &amp; Cookies</strong>
            <span>Browser type, operating system, and cookies for analytics.</span>
          </div>
        </div>
        <div className="highlight-box">
          <i className="fa-regular fa-circle-check" />
          We only collect data that is necessary to fulfill your bookings
          and improve our services.
        </div>
      </>
    )
  },
  {
    id: 'use',
    icon: 'fa-solid fa-arrow-trend-up',
    title: 'How We Use Your Information',
    content: (
      <>
        <p>We use your information to:</p>
        <ul>
          <li><strong>Process bookings</strong> — confirm, schedule, and manage technician appointments.</li>
          <li><strong>Communicate</strong> — send booking confirmations, reminders, and service updates.</li>
          <li><strong>Improve our platform</strong> — analyse usage patterns to enhance user experience.</li>
          <li><strong>Personalise</strong> — recommend services and offers based on your preferences.</li>
          <li><strong>Ensure security</strong> — detect and prevent fraud, abuse, or other malicious activity.</li>
        </ul>
        <p style={{ marginTop: '0.5rem', fontSize: '0.92rem', color: '#64748b' }}>
          <i className="fa-regular fa-envelope" style={{ marginRight: '0.4rem' }} />
          You can opt out of marketing emails at any time.
        </p>
      </>
    )
  },
  {
    id: 'share',
    icon: 'fa-solid fa-share-nodes',
    title: 'Information Sharing & Disclosure',
    content: (
      <>
        <p>
          We do <strong>not</strong> sell your personal data. We may share
          your information only in these limited cases:
        </p>
        <ul>
          <li>
            <strong>With technicians</strong> — to fulfil your booking
            (name, contact, service address).
          </li>
          <li>
            <strong>Service providers</strong> — payment processors,
            cloud hosting, and analytics (all GDPR/CCPA compliant).
          </li>
          <li>
            <strong>Legal compliance</strong> — if required by law or to
            protect the rights and safety of OneClick Tech and its users.
          </li>
        </ul>
        <div className="highlight-box">
          <i className="fa-regular fa-handshake" />
          We require all third‑party partners to adhere to strict
          privacy and security standards.
        </div>
      </>
    )
  },
  {
    id: 'security',
    icon: 'fa-solid fa-lock',
    title: 'Data Security',
    content: (
      <>
        <p>
          We implement industry‑standard measures to protect your data:
        </p>
        <ul>
          <li><strong>Encryption</strong> — all data transmitted via TLS/SSL.</li>
          <li><strong>Access control</strong> — strict role‑based access to personal data.</li>
          <li><strong>Regular audits</strong> — vulnerability assessments and penetration testing.</li>
          <li><strong>Secure storage</strong> — data stored in EU‑based data centres with ISO 27001 certification.</li>
        </ul>
        <p style={{ marginTop: '0.5rem', fontSize: '0.92rem', color: '#64748b' }}>
          <i className="fa-regular fa-triangle-exclamation" style={{ marginRight: '0.4rem', color: '#f59e0b' }} />
          While we strive for 100% security, no system is infallible. We
          will notify you promptly in case of a data breach.
        </p>
      </>
    )
  },
  {
    id: 'cookies',
    icon: 'fa-solid fa-cookie-bite',
    title: 'Cookies & Tracking',
    content: (
      <>
        <p>
          We use cookies and similar tracking technologies to enhance your
          experience. Cookies help us:
        </p>
        <ul>
          <li>Remember your preferences and login session.</li>
          <li>Analyse traffic and improve our platform.</li>
          <li>Deliver relevant service recommendations.</li>
        </ul>
        <p>
          You can manage cookie preferences in your browser settings.
          Disabling cookies may affect some functionality.
        </p>
        <div className="highlight-box">
          <i className="fa-regular fa-sliders" />
          We use <strong>essential</strong>, <strong>functional</strong>,
          and <strong>analytics</strong> cookies. We do <strong>not</strong>{' '}
          use intrusive third‑party advertising cookies.
        </div>
      </>
    )
  },
  {
    id: 'rights',
    icon: 'fa-solid fa-scale-balanced',
    title: 'Your Privacy Rights',
    content: (
      <>
        <p>
          Depending on your location (GDPR, CCPA, etc.), you have the
          following rights:
        </p>
        <ul>
          <li><strong>Access</strong> — request a copy of your personal data.</li>
          <li><strong>Rectification</strong> — correct inaccurate or incomplete data.</li>
          <li><strong>Erasure</strong> — request deletion of your data ("right to be forgotten").</li>
          <li><strong>Restriction</strong> — limit how we process your data.</li>
          <li><strong>Portability</strong> — receive your data in a structured format.</li>
          <li><strong>Object</strong> — object to processing for marketing or profiling.</li>
        </ul>
        <p style={{ marginTop: '0.5rem' }}>
          To exercise any of these rights, please <a href="#contact" style={{ color: '#e5243b', textDecoration: 'none', fontWeight: 500 }}>contact us</a>.
          We will respond within 30 days.
        </p>
      </>
    )
  },
  {
    id: 'updates',
    icon: 'fa-regular fa-pen-to-square',
    title: 'Updates to This Policy',
    content: (
      <>
        <p>
          We may update this Privacy Policy from time to time. We will
          notify you of any changes by posting the new policy on this page
          and updating the "Last Updated" date at the top.
        </p>
        <p>
          We encourage you to review this policy periodically. Continued
          use of OneClick Tech after changes constitutes your acceptance
          of the revised policy.
        </p>
      </>
    )
  },
  {
    id: 'contact',
    icon: 'fa-regular fa-envelope',
    title: 'Contact Us',
    content: (
      <>
        <p>
          If you have any questions, concerns, or requests regarding this
          Privacy Policy, please reach out to us:
        </p>
        <div className="contact-card">
          <div className="contact-info">
            <i className="fa-regular fa-envelope" />
            <div>
              <span className="label">Email</span>
              <span className="value">privacy@oneclickservices.com</span>
            </div>
          </div>
          <div className="contact-info">
            <i className="fa-regular fa-phone" />
            <div>
              <span className="label">Phone</span>
              <span className="value">+1 (800) 123‑4567</span>
            </div>
          </div>
          <button className="btn-contact" onClick={() => alert('📧 Privacy team: privacy@oneclickservices.com')}>
            <i className="fa-regular fa-paper-plane" /> Send Message
          </button>
        </div>
      </>
    )
  }
];

// ─── NavLink helper ────────────────────────────────────
const NavLink = ({ href, children, active, onClick }) => (
  <a
    href={href}
    className={active ? 'active' : ''}
    onClick={(e) => {
      e.preventDefault();
      if (onClick) onClick(href);
    }}
  >
    {children}
  </a>
);

// ─── Main Component ────────────────────────────────────
const PrivacyPolicy = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('intro');

  const toggleMobile = () => setMobileOpen(prev => !prev);

  const handleNavClick = (href) => {
    const id = href.replace('#', '');
    setActiveSection(id);
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Update active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = policySections.map(s => s.id);
      const scrollY = window.scrollY + 120;

      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const { offsetTop, offsetHeight } = el;
          if (scrollY >= offsetTop && scrollY < offsetTop + offsetHeight) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="privacy-app">


      {/* Page Header */}
      <header className="page-header">
        <h1>
          <i className="fa-solid fa-shield-halved" />
          Privacy Policy
        </h1>
        <p>Your trust matters. Learn how we protect your data at OneClick Tech.</p>
        <span className="last-updated">
          <i className="fa-regular fa-calendar" style={{ marginRight: '0.4rem' }} />
          Last Updated: August 3, 2026
        </span>
      </header>

      {/* Main Content */}
      <main className="privacy-content">
        {policySections.map((section) => (
          <section key={section.id} id={section.id} className="policy-card">
            <div className="section-icon">
              <i className={section.icon} />
            </div>
            <h2>
              <i className={section.icon} />
              {section.title}
            </h2>
            {section.content}
          </section>
        ))}

        <div style={{
          textAlign: 'center',
          padding: '1.5rem 1rem',
          background: 'transparent',
          fontSize: '0.9rem',
          color: '#94a3b8',
          borderTop: '1px solid #eef2f6',
          marginTop: '1rem'
        }}>
          <i className="fa-regular fa-circle-check" style={{ color: '#22c55e', marginRight: '0.5rem' }} />
          This policy complies with GDPR, CCPA, and other applicable privacy regulations.
        </div>
      </main>


    </div>
  );
};

export default PrivacyPolicy;